import { isFullProjectContentBlock, type FullProjectContentBlock } from "../types.js";
import { formatProjectDuration } from "../dateUtils.js";
import { sharedSheet } from "../styles/sharedStyles.js";
import componentStylesText from "./ProjectBlockComponent.css?inline";
import "./ExpandableBlockComponent.js";

const componentSheet = new CSSStyleSheet();
componentSheet.replaceSync(componentStylesText);

/**
 * ProjectBlockComponent — A leaf component for rendering a project/experience entry in the chat.
 *
 * It renders a preview and provides an inline expansion for full details.
 *
 * Attributes:
 * - block: The JSON stringified project block object.
 */
export class ProjectBlockComponent extends HTMLElement {
    private expanded: boolean = false;

    static get observedAttributes(): string[] {
        return ["block", "role"];
    }

    constructor() {
        super();
    }

    /**
     * Returns the primary text content for accessibility aggregation.
     */
    get accessibilityText(): string {
        const data = this.getProjectData();
        if (!data) {
            return "";
        }
        const durationText = formatProjectDuration(data.start, data.end);
        let text = `Project: ${data.title} at ${data.company.name} from ${durationText} focusing on ${data.focusDescription}`;
        
        if (this.expanded && data.bulletPoints.length > 0) {
            text += ". Key achievements: " + data.bulletPoints.join(", ");
        }
        
        return text;
    }

    private getProjectData(): FullProjectContentBlock | null {
        const blockAttr = this.getAttribute("block");
        if (!blockAttr) {
            return null;
        }
        try {
            const parsed: unknown = JSON.parse(blockAttr);
            return isFullProjectContentBlock(parsed) ? parsed : null;
        } catch {
            return null;
        }
    }

    connectedCallback(): void {
        if (!this.shadowRoot) {
            const shadow = this.attachShadow({ mode: "open" });
            shadow.adoptedStyleSheets = [sharedSheet, componentSheet];
            this.render();
        }
        this.updateProject();
        this.dispatchEvent(new CustomEvent("text-update", { bubbles: true }));
    }

    attributeChangedCallback(name: string, _oldValue: string | null, _newValue: string | null): void {
        if (name === "block" || name === "role") {
            this.updateProject();
            this.dispatchEvent(new CustomEvent("text-update", { bubbles: true }));
        }
    }

    private render(): void {
        const expandable = document.createElement("jt-expandable-block");
        expandable.setAttribute("expand-button-label", "Show full project info");
        expandable.setAttribute("collapse-button-label", "Hide full project info");
        
        // Handle expansion changes
        expandable.addEventListener("toggle-request", (e) => {
            const detail = (e as CustomEvent<{ expanded: boolean }>).detail;
            this.expanded = detail.expanded;
            this.updateExpansionState();
            this.dispatchEvent(new CustomEvent("text-update", { bubbles: true }));
        });

        const root = document.createElement("div");
        root.className = "project-block";

        const header = document.createElement("div");
        header.className = "project-block__header";
        
        const body = document.createElement("div");
        body.className = "project-block__body";

        const detailsRegion = document.createElement("div");
        detailsRegion.className = "project-block__details";
        detailsRegion.setAttribute("aria-hidden", "true");
        detailsRegion.setAttribute("inert", "");

        root.append(header, body, detailsRegion);
        expandable.append(root);
        this.shadowRoot?.appendChild(expandable);
    }

    private updateProject(): void {
        const expandable = this.shadowRoot?.querySelector("jt-expandable-block");
        if (!expandable) {
            return;
        }

        const data = this.getProjectData();
        const role = this.getAttribute("role") || "assistant";
        expandable.setAttribute("role", role);

        const root = expandable.querySelector(".project-block");
        const header = root?.querySelector(".project-block__header") as HTMLElement;
        const body = root?.querySelector(".project-block__body") as HTMLElement;
        const detailsRegion = root?.querySelector(".project-block__details") as HTMLElement;

        if (!header || !body || !detailsRegion) {
            // Something went wrong, recreate placeholders?
            // This case should not happen if render() works as expected.
            return;
        }

        if (!data) {
            this.expanded = false;
            header.replaceChildren();
            body.replaceChildren();
            detailsRegion.replaceChildren();
            this.updateExpansionState();
            return;
        }

        // Fill Header
        header.replaceChildren();
        if (data.company.logo) {
            const logo = document.createElement("img");
            logo.src = data.company.logo;
            logo.alt = "";
            logo.className = "project-block__company-logo";
            header.appendChild(logo);
        }

        const companyInfo = document.createElement("div");
        companyInfo.className = "project-block__company-info";

        const companyName = document.createElement("p");
        companyName.className = "project-block__company-name";
        companyName.textContent = data.company.name;
        companyInfo.appendChild(companyName);

        const duration = document.createElement("p");
        duration.className = "project-block__duration";
        duration.textContent = formatProjectDuration(data.start, data.end);
        companyInfo.appendChild(duration);

        header.appendChild(companyInfo);

        // Fill Body
        body.replaceChildren();
        const title = document.createElement("h3");
        title.className = "project-block__title";
        title.textContent = data.title;
        body.appendChild(title);

        const focus = document.createElement("p");
        focus.className = "project-block__focus";
        focus.textContent = data.focusDescription;
        body.appendChild(focus);

        // Fill Details (Bullet Points)
        detailsRegion.replaceChildren();
        const bulletPoints = document.createElement("ul");
        bulletPoints.className = "project-detail__bullet-points";
        
        data.bulletPoints.forEach(point => {
            const li = document.createElement("li");
            li.textContent = point;
            bulletPoints.appendChild(li);
        });

        detailsRegion.appendChild(bulletPoints);

        this.updateExpansionState();
    }

    private updateExpansionState(): void {
        const expandable = this.shadowRoot?.querySelector("jt-expandable-block");
        const detailsRegion = expandable?.querySelector(".project-block__details") as HTMLElement;
        if (!expandable || !detailsRegion) {
            return;
        }

        const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

        if (this.expanded) {
            (expandable as HTMLElement).setAttribute("expanded", "");
            detailsRegion.removeAttribute("aria-hidden");
            detailsRegion.removeAttribute("inert");
            
            if (prefersReducedMotion) {
                this.setHeight(detailsRegion, "auto");
            } else {
                this.setHeight(detailsRegion, `${detailsRegion.scrollHeight}px`);
            }
        } else {
            (expandable as HTMLElement).removeAttribute("expanded");
            detailsRegion.setAttribute("aria-hidden", "true");
            detailsRegion.setAttribute("inert", "");
            this.setHeight(detailsRegion, "0px");
        }
    }

    /**
     * Guideline Exception: Direct .style.height mutation is used here to allow
     * smooth CSS transitions to/from a runtime-calculated height (scrollHeight).
     */
    private setHeight(element: HTMLElement, height: string): void {
        element.style.height = height;
    }
}

if (!customElements.get("jt-project-block")) {
    customElements.define("jt-project-block", ProjectBlockComponent);
}
